import React from "react";

function stripHtmlLike(input: string) {
  return input.replace(/<script[\s\S]*?<\/script>/gi, "").replace(/<style[\s\S]*?<\/style>/gi, "").replace(/<\/?[^>]+>/g, "");
}

function trimUrl(rawUrl: string) {
  const match = rawUrl.match(/^(.*?)([)、。.,）」』】\]]*)$/);
  return {
    url: match?.[1] || rawUrl,
    trailing: match?.[2] || "",
  };
}

function getSafeHttpHref(rawHref: string) {
  const trimmed = rawHref.trim();
  if (!/^https?:\/\//i.test(trimmed)) return null;

  try {
    const url = new URL(trimmed);
    return url.protocol === "http:" || url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

function createExternalLink(key: string, href: string, label: React.ReactNode) {
  return (
    <a
      key={key}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="break-words font-bold text-[#A67C52] underline underline-offset-2 [overflow-wrap:anywhere]"
    >
      {label}
    </a>
  );
}

function pushPlainTextWithLinks(parts: React.ReactNode[], text: string, keyPrefix: string) {
  const urlPattern = /https?:\/\/[^\s<]+/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = urlPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const { url, trailing } = trimUrl(match[0]);
    const href = getSafeHttpHref(url);
    if (href) {
      parts.push(createExternalLink(`${keyPrefix}-url-${match.index}-${href}`, href, url));
    } else {
      parts.push(match[0]);
    }
    if (trailing) {
      parts.push(trailing);
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
}

function renderInlineMarkdown(text: string, keyPrefix: string) {
  const strippedText = stripHtmlLike(text);
  const parts: React.ReactNode[] = [];
  const inlinePattern = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*\n]+)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = inlinePattern.exec(strippedText)) !== null) {
    if (match.index > lastIndex) {
      pushPlainTextWithLinks(parts, strippedText.slice(lastIndex, match.index), `${keyPrefix}-plain-${match.index}`);
    }

    const linkHref = match[2];
    const boldText = match[3];
    if (linkHref) {
      const href = getSafeHttpHref(linkHref);
      const label = stripHtmlLike(match[1]);
      parts.push(href ? createExternalLink(`${keyPrefix}-link-${match.index}-${href}`, href, label) : match[0]);
    } else if (boldText) {
      parts.push(
        <strong key={`${keyPrefix}-bold-${match.index}`} className="font-black text-[#4A3E39]">
          {renderInlineMarkdown(boldText, `${keyPrefix}-bold-${match.index}`)}
        </strong>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < strippedText.length) {
    pushPlainTextWithLinks(parts, strippedText.slice(lastIndex), `${keyPrefix}-tail-${lastIndex}`);
  }

  return parts.length > 0 ? parts : strippedText;
}

type ListState = {
  kind: "ul" | "ol";
  items: string[];
};

function parseMarkdownTableRow(line: string) {
  const trimmed = line.trim();
  if (!trimmed.startsWith("|") || !trimmed.endsWith("|")) return null;

  return trimmed
    .slice(1, -1)
    .split("|")
    .map((cell) => cell.trim());
}

function isMarkdownTableSeparator(line: string) {
  const cells = parseMarkdownTableRow(line);
  return Boolean(cells && cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell)));
}

type SafeMarkdownTableVariant = "default" | "artifact";

type SafeMarkdownProps = {
  text: string;
  tableVariant?: SafeMarkdownTableVariant;
};

export function SafeMarkdown({ text, tableVariant = "default" }: SafeMarkdownProps) {
  const lines = text.split(/\r?\n/);
  const elements: React.ReactNode[] = [];
  let listState: ListState | null = null;
  const isArtifactTableVariant = tableVariant === "artifact";
  const proseWidthClassName = isArtifactTableVariant ? "max-w-[760px]" : "";
  const withProseWidth = (className: string) => [className, proseWidthClassName].filter(Boolean).join(" ");

  const flushList = () => {
    if (!listState || listState.items.length === 0) return;
    const current = listState;
    listState = null;

    const listClassName = withProseWidth(
      "my-3 space-y-2 break-words pl-5 text-sm leading-7 text-[#5D4D46] [overflow-wrap:anywhere]"
    );
    const children = current.items.map((item, index) => (
      <li key={`${item}-${index}`} className="min-w-0">
        {renderInlineMarkdown(item, `list-${elements.length}-${index}`)}
      </li>
    ));

    elements.push(
      current.kind === "ol" ? (
        <ol key={`ol-${elements.length}`} className={`${listClassName} list-decimal`}>
          {children}
        </ol>
      ) : (
        <ul key={`ul-${elements.length}`} className={`${listClassName} list-disc`}>
          {children}
        </ul>
      )
    );
  };

  for (let index = 0; index < lines.length; index += 1) {
    const rawLine = lines[index];
    const line = rawLine.trim();
    if (!line) {
      flushList();
      continue;
    }

    const headerCells = parseMarkdownTableRow(line);
    const separatorLine = lines[index + 1]?.trim() ?? "";
    if (headerCells && isMarkdownTableSeparator(separatorLine)) {
      flushList();
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length) {
        const rowCells = parseMarkdownTableRow(lines[index].trim());
        if (!rowCells) break;
        rows.push(rowCells);
        index += 1;
      }
      index -= 1;
      const tableWrapperClassName = isArtifactTableVariant
        ? "my-4 max-w-full overflow-x-auto rounded-lg border border-[#EADBC8] [overscroll-behavior-x:contain]"
        : "my-4 overflow-x-auto rounded-xl border border-[#EADBC8]";
      const tableClassName = isArtifactTableVariant
        ? "min-w-[44rem] table-auto border-collapse bg-white text-left text-xs text-[#5D4D46] sm:text-sm"
        : "min-w-full border-collapse bg-white text-left text-sm text-[#5D4D46]";
      const tableHeaderCellClassName = isArtifactTableVariant
        ? "min-w-[9rem] whitespace-nowrap border-b border-[#EADBC8] px-3 py-2 font-black"
        : "border-b border-[#EADBC8] px-3 py-2 font-black";
      const tableCellClassName = isArtifactTableVariant
        ? "min-w-[10rem] align-top px-3 py-2 leading-6"
        : "align-top px-3 py-2 leading-6";

      elements.push(
        <div
          key={`table-${elements.length}`}
          data-testid="safe-markdown-table-scroll"
          className={tableWrapperClassName}
        >
          <table className={tableClassName}>
            <thead className="bg-[#FFF4EC]">
              <tr>
                {headerCells.map((cell, cellIndex) => (
                  <th key={`th-${cellIndex}`} className={tableHeaderCellClassName}>
                    {renderInlineMarkdown(cell, `table-${elements.length}-head-${cellIndex}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`tr-${rowIndex}`} className="border-t border-[#F2E3D8]">
                  {headerCells.map((_, cellIndex) => (
                    <td key={`td-${rowIndex}-${cellIndex}`} className={tableCellClassName}>
                      {renderInlineMarkdown(row[cellIndex] ?? "", `table-${elements.length}-row-${rowIndex}-${cellIndex}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    const orderedListMatch = line.match(/^\d+\.\s+(.+)$/);
    if (orderedListMatch) {
      if (!listState || listState.kind !== "ol") {
        flushList();
        listState = { kind: "ol", items: [] };
      }
      listState.items.push(orderedListMatch[1]);
      continue;
    }

    if (line.startsWith("- ") || line.startsWith("* ")) {
      if (!listState || listState.kind !== "ul") {
        flushList();
        listState = { kind: "ul", items: [] };
      }
      listState.items.push(line.slice(2));
      continue;
    }

    flushList();

    if (line.startsWith("### ")) {
      elements.push(
        <h3 key={`h3-${index}`} className={withProseWidth("mt-5 break-words text-base font-black text-[#5D4D46] first:mt-0 [overflow-wrap:anywhere]")}>
          {renderInlineMarkdown(line.slice(4), `h3-${index}`)}
        </h3>
      );
      continue;
    }

    if (line.startsWith("## ")) {
      elements.push(
        <h2 key={`h2-${index}`} className={withProseWidth("mt-6 break-words text-lg font-black text-[#5D4D46] first:mt-0 [overflow-wrap:anywhere]")}>
          {renderInlineMarkdown(line.slice(3), `h2-${index}`)}
        </h2>
      );
      continue;
    }

    if (line.startsWith("# ")) {
      elements.push(
        <h1 key={`h1-${index}`} className={withProseWidth("mt-6 break-words text-xl font-black text-[#5D4D46] first:mt-0 [overflow-wrap:anywhere]")}>
          {renderInlineMarkdown(line.slice(2), `h1-${index}`)}
        </h1>
      );
      continue;
    }

    elements.push(
      <p key={`p-${index}`} className={withProseWidth("my-3 break-words text-sm leading-7 text-[#5D4D46] [overflow-wrap:anywhere]")}>
        {renderInlineMarkdown(line, `p-${index}`)}
      </p>
    );
  }

  flushList();

  return <div>{elements}</div>;
}
